import React, { memo, useCallback, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { ActionsWrapper, LeftActionsWrapper } from '@atlassian/jira-business-app-wrapper';
import { Filters, SimpleSearchField, useHideDoneItems } from '@atlassian/jira-business-filters';
import {
	JWMShareButton,
	integrationTypes,
	AddPeopleWithAvatar,
} from '@atlassian/jira-business-invite-people';
import { MoreMenu } from '@atlassian/jira-business-more-menu';
import {
	TIMELINE_EMBED_NUDGE_STEP,
	useNudgeStep,
} from '@atlassian/jira-business-onboarding-timeline-embed';
import { useIntl } from '@atlassian/jira-intl';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { useTimelineFeatures } from '../../../controllers/features-context';
import messages from './messages';
import TodayButton from './today-button';

export type ToolbarProps = {
	exportToImageButton: ReactNode;
};

const Toolbar = ({ exportToImageButton }: ToolbarProps) => {
	const [hideDoneItems, setHideDoneItems] = useHideDoneItems();
	const [{ nudgeStep, shouldOpenModal }, { setShouldOpenModal }] = useNudgeStep();
	const {
		hasShareButton,
		hasExportButton,
		inviteButtonInteraction,
		hasPresence,
		hasInviteButton,
		hasFilterCustomFields,
	} = useTimelineFeatures();

	const handleDialogClose = useCallback(() => {
		if (shouldOpenModal) {
			setShouldOpenModal(false);
		}
	}, [setShouldOpenModal, shouldOpenModal]);
	const { formatMessage } = useIntl();

	const showShareButtonSpotlight = nudgeStep === TIMELINE_EMBED_NUDGE_STEP;

	const shareButton = (
		<JWMShareButton
			shareTitle={messages.shareTitle}
			integrationType={integrationTypes.TIMELINE}
			shareContentType="timeline"
			isAutoOpenDialog={shouldOpenModal}
			onDialogClose={handleDialogClose}
		/>
	);

	const wrappedShareButton = showShareButtonSpotlight ? (
		<SpotlightTarget name={TIMELINE_EMBED_NUDGE_STEP}>{shareButton}</SpotlightTarget>
	) : (
		shareButton
	);

	const searchBarLabelId = 'header-actions-search-field-label';

	const searchBar = (
		<>
			<VisuallyHidden id={searchBarLabelId}>{formatMessage(messages.searchLabel)}</VisuallyHidden>
			<SimpleSearchField
				placeholder={messages.simpleSearch}
				view="timeline"
				describedbyId={searchBarLabelId}
			/>
		</>
	);

	const addPeopleWithPresence = (
		<AddPeopleWithAvatar
			view="timeline"
			isPresenceEnabledInContext={hasPresence}
			hasInviteButton={hasInviteButton}
			isOpenNewTabOnClick={inviteButtonInteraction === 'openNewTab'}
		/>
	);

	const todayButton = <TodayButton />;

	const shareActionButton = hasShareButton && wrappedShareButton;

	const exportButton = hasExportButton && exportToImageButton;

	const filters = <Filters view="timeline" allowCustomFields={hasFilterCustomFields} />;

	const moreMenu = (
		<MoreMenu hideDoneItems={Boolean(hideDoneItems)} onChangeHideDoneItems={setHideDoneItems} />
	);

	return (
		<UFOSegment name="business-timeline-toolbar">
			<ActionsWrapper>
				<LeftActionsWrapper>
					{searchBar}
					{addPeopleWithPresence}
				</LeftActionsWrapper>

				<UnorderedListWrapper>
					<ListItemWrapper>{todayButton}</ListItemWrapper>
					<ListItemWrapper>{shareActionButton}</ListItemWrapper>
					<ListItemWrapper>{exportButton}</ListItemWrapper>
					<ListItemWrapper>{filters}</ListItemWrapper>
					<ListItemWrapper>{moreMenu}</ListItemWrapper>
				</UnorderedListWrapper>
			</ActionsWrapper>
		</UFOSegment>
	);
};

export default memo<ToolbarProps>(Toolbar);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnorderedListWrapper = styled.ul({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100', '8px'),
	padding: 0,
	margin: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListItemWrapper = styled.li({
	display: 'flex',
	alignItems: 'center',
});
